import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import { connect } from 'react-redux';
import PortalIdParser from 'PortalIdParser';
import shallowCompare from 'react-addons-shallow-compare';
import Routes from 'ContentUtils/Routes';
import { isEqual } from 'underscore';
import CtaBase from './CtaBase';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIAlert from 'UIComponents/alert/UIAlert';
import UILink from 'UIComponents/link/UILink';
import * as CtaActions from 'ContentData/actions/Ctas';
import * as webInteractiveActions from 'ContentData/actions/WebInteractives';
import * as ctaSelectors from 'ContentData/selectors/ctas';
import * as webInteractiveSelectors from 'ContentData/selectors/webInteractives';
import { CTA_PICKER_STATE } from 'ContentComponents/cmv2/constants';
import PickersContext from 'ContentComponents/context/PickersContext';
import { checkHasAccessToCtas, checkHasAccessToLegacyCtas, checkIsWebInteractive, openWebInteractivesEditor } from 'ContentComponents/utils/ctaUtils';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const makeMapStateToProps = () => {
  const getSelectedCtaImage = ctaSelectors.makeGetSelectedCtaImage();
  return (state, props) => {
    const guid = props.value;
    const isWebInteractive = checkIsWebInteractive(guid);
    let cta;
    let selectedCtaImage;
    let hasLoaded;
    if (isWebInteractive) {
      const id = Number(guid);
      cta = webInteractiveSelectors.getWebInteractiveInLegacyShape(state, {
        id,
        isInEmail: props.isInEmail
      });
      selectedCtaImage = cta ? cta.ctaInfo[0].image : null;
      hasLoaded = webInteractiveSelectors.getIsWebInteractiveFetchDone(state, {
        id
      });
    } else {
      cta = ctaSelectors.getCta(state, {
        guid
      });
      selectedCtaImage = getSelectedCtaImage(state, {
        guid
      });
      hasLoaded = ctaSelectors.getIsCtaFetchDone(state, {
        guid
      });
    }
    return {
      cta,
      selectedCtaImage,
      hasLoaded
    };
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchCta: guid => checkIsWebInteractive(ownProps.value) ? dispatch(webInteractiveActions.fetchWebInteractive(guid)) : dispatch(CtaActions.fetchCta(guid))
});
const connector = connect(makeMapStateToProps, mapDispatchToProps);
const i18nKey = key => `ContentComponents.cmv2.fields.cta.${key}`;
class Cta extends Component {
  constructor(props) {
    super(props);
    this.swapOutIds = contentId => {
      this.onInsertCta(null, contentId);
    };
    this.trackPQLClick = this.trackPQLClick.bind(this);
    this.onInsertCta = this.onInsertCta.bind(this);
    this.onClosePicker = this.onClosePicker.bind(this);
    this.onOpenPicker = this.onOpenPicker.bind(this);
    this.onOpenEditor = this.onOpenEditor.bind(this);
    this.state = {
      hasCtaAccess: false,
      hasLegacyCtaAccess: false,
      showPicker: CTA_PICKER_STATE.CLOSED,
      input: ''
    };
  }
  componentDidMount() {
    const {
      fetchCta,
      value = null
    } = this.props;
    if (value) {
      fetchCta(value);
    }
    this.setState({
      hasCtaAccess: checkHasAccessToCtas(),
      hasLegacyCtaAccess: checkHasAccessToLegacyCtas()
    });
  }
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState);
  }
  componentDidUpdate(prevProps) {
    const {
      cta,
      fetchCta,
      value = null
    } = this.props;
    const {
      value: prevValue = null
    } = prevProps;
    if (!isEqual(prevValue, value) && !cta) {
      fetchCta(value).catch(err => {
        throw err;
      });
    }
  }
  trackPQLClick() {
    const {
      trackFieldAction
    } = this.props;
    if (trackFieldAction) {
      trackFieldAction('cta-pql-click');
    }
  }
  onInsertCta(_, guid) {
    const {
      fetchCta,
      onFieldChange = emptyFunction
    } = this.props;
    fetchCta(guid).then(() => {
      onFieldChange(guid);
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
    this.onClosePicker();
  }
  onOpenPicker() {
    this.setState({
      showPicker: CTA_PICKER_STATE.OPEN
    });
  }
  onOpenEditor() {
    const {
      value = null
    } = this.props;
    if (checkIsWebInteractive(value)) {
      openWebInteractivesEditor(value);
    } else {
      this.setState({
        showPicker: CTA_PICKER_STATE.EDIT
      });
    }
  }
  onClosePicker() {
    this.setState({
      showPicker: CTA_PICKER_STATE.CLOSED
    });
  }
  renderCtaAccessWarning() {
    const upgradeLink = /*#__PURE__*/_jsx(UILink, {
      external: true,
      href: Routes.marketingHubPricingPage(),
      onClick: this.trackPQLClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`missingScope.upgradeLinkText`)
      })
    });
    return /*#__PURE__*/_jsx(UIAlert, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`missingScope.title`)
      }),
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey(`missingScope.message`),
        options: {
          upgradeLink
        }
      })
    });
  }
  renderLegacyCtaWarning() {
    return /*#__PURE__*/_jsx(UIAlert, {
      "data-test-id": "cmv2-cta-legacy-warning",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`missingLegacyScope.title`)
      }),
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`missingLegacyScope.message`)
      })
    });
  }
  renderCtaPicker() {
    const {
      value = null,
      isVideo = false,
      includeVideoCtas = false
    } = this.props;
    const {
      showPicker
    } = this.state;
    if (showPicker === CTA_PICKER_STATE.CLOSED) {
      return null;
    }
    return /*#__PURE__*/_jsx(PickersContext.Consumer, {
      children: ({
        CtaPicker,
        FilePicker
      }) => /*#__PURE__*/_jsx(CtaPicker, {
        portalId: PortalIdParser.get(),
        handleInsert: this.onInsertCta,
        handleClose: this.onClosePicker,
        selectedGuid: showPicker === CTA_PICKER_STATE.EDIT ? value : null,
        FilePicker: FilePicker,
        isVideo: isVideo,
        includeVideoCtas: includeVideoCtas
      })
    });
  }
  renderCtaMigratedWarning() {
    const {
      cta
    } = this.props;
    const {
      migratedWebInteractiveContentId
    } = cta;
    return /*#__PURE__*/_jsxs(UIAlert, {
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`migrated.title`)
      }),
      type: "warning",
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey(`migrated.message`)
      }), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "100",
        as: "div",
        children: /*#__PURE__*/_jsx(UILink, {
          onClick: () => this.swapOutIds(migratedWebInteractiveContentId),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`migrated.swapOutMessage`)
          })
        })
      })]
    });
  }
  render() {
    const {
      hasCtaAccess,
      hasLegacyCtaAccess
    } = this.state;
    const {
      cta,
      value = null
    } = this.props;
    const isLegacyCtaWithoutLegacyAccess = Boolean(hasCtaAccess && !hasLegacyCtaAccess && !checkIsWebInteractive(value) && cta);
    return /*#__PURE__*/_jsxs("div", {
      children: [!hasCtaAccess && this.renderCtaAccessWarning(), isLegacyCtaWithoutLegacyAccess && this.renderLegacyCtaWarning(), cta && cta.isMigrated && this.renderCtaMigratedWarning(), /*#__PURE__*/_jsx(CtaBase, Object.assign({}, this.props, isLegacyCtaWithoutLegacyAccess ? {
        disableEdit: true
      } : {}, {
        hasCtaAccess: hasCtaAccess,
        onOpenPicker: this.onOpenPicker,
        onOpenEditor: this.onOpenEditor
      })), this.renderCtaPicker()]
    });
  }
}
export default connector(Cta);